import { Button, EmbedUrlRequest, Skeleton } from 'vopay';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useToast from '#shared/providers/Toast/useToast';
import useCreateEmbedUrl from './useCreateEmbedUrl';
import styles from './ELinxConnect.module.scss';

export type ELinxConnectProps = {
  setting: EmbedUrlRequest;
  onSuccess?: (eventValues: EventMessage) => void;
  onClose: () => void;
  loadingMessage?: ReactNode;
  // Renders the flow embedded rather than as a full-screen takeover: the container becomes
  // position:absolute with a transparent background, so it fills its nearest positioned ancestor (a
  // relative/absolute wrapper) instead of the viewport, and the surrounding surface shows through.
  embedded?: boolean;
};
export type EventMessageData = {
  Step: 'LINK';
  Institution: string;
  Url: string;
  Token: string;
  BankId: string;
  PaymentMethod: 'bank' | 'creditcard' | 'debitcard';
  CreditCardNumber: string;
  CreditCardHolderName: string;
  CreditCardBrand: string;
  CreditCardExpiryYear: string;
  CreditCardExpiryMonth: string;
  [key: string]: string;
};
export type EventMessage = {
  data: EventMessageData;
};

// The window `message` listener is fed by any frame/extension/tab that can postMessage to us, so the
// payload is untrusted: narrow it before reading. A null or string `data` would otherwise throw on
// `.Step`, and a malformed object would be forwarded as a linked payment method. We deliberately do NOT
// also pin `event.origin`/`event.source` to the iframe here: the real eLinx posting origin isn't fixed
// (GlobalSend/BillPayment defer the same check), and the Cypress harness stands in for the iframe by
// posting from the app window — tightening the source is a separate hardening task, not this guard.
function isLinkMessage(data: unknown): data is EventMessageData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'Step' in data &&
    data.Step === 'LINK' &&
    'Token' in data &&
    typeof data.Token === 'string'
  );
}

export default function ELinxConnect({ setting, onSuccess, onClose, loadingMessage, embedded }: ELinxConnectProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ELinxConnect' });
  const toast = useToast();
  const { createEmbedUrl } = useCreateEmbedUrl();
  const [url, setUrl] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isLinkMessage(event.data)) {
        onSuccess?.({ data: event.data });
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    createEmbedUrl(setting)
      .then((res) => {
        if (!cancelled) setUrl(res.EmbedURL);
      })
      .catch(() => {
        // The hook already logged; surface the failure so the container isn't left blank.
        if (!cancelled) {
          toast.error(t('embedUrlError', { defaultValue: "We couldn't load the connection form. Please try again." }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [setting]);

  return (
    <div className={[styles.iframeContainer, embedded && styles.embedded].filter(Boolean).join(' ')}>
      {url && <iframe className={styles.iframe} src={url} title="eLinx" seamless onLoad={() => setLoaded(true)} />}
      {/* Localized loading overlay, layered on top of the iframe until it fires onLoad. Covers both the
          embed-URL GET and the iframe's own network load — an unloaded iframe paints an opaque blank box,
          so a loader placed behind it would be hidden until eLinx paints. Not the fullscreen blocking
          loader (this is a GET that fills an iframe, not a blocking submit); a caller loadingMessage wins. */}
      {!loaded && (loadingMessage ?? <Skeleton height="100%" containerClassName={styles.loadingSkeleton} />)}

      <div className={styles.iframeClose}>
        <Button
          id="close-elinx"
          label=""
          noShadow
          noPadding
          height="50px"
          width="50px"
          onClick={onClose}
          icon="close"
          iconLocation="center"
          iconSize="xlg"
          color="secondary"
        />
      </div>
    </div>
  );
}
